// qs-ResetWarningModal.vue
/**
 * Last Modified: 2024-12-19 - Created reset warning modal
 * 
 * PURPOSE: Simple warning modal for cache reset functionality
 * REASON: User requested a warning modal with cancel/reset buttons for the cache reset feature
 * 
 * This file was created on 2024-12-19 to address:
 * - Need for user confirmation before clearing all data
 * - Better UX for destructive reset action
 * - Simple modal with clear warning message
 */

<template>
  <div
    v-if="isVisible"
    class="modal-overlay"
    @click="handleOverlayClick"
  >
    <div
      class="modal-content"
      @click.stop
    >
      <div class="modal-header">
        <h3 class="modal-title">
          ⚠️ Reset Cache
        </h3>
      </div>
      
      <div class="modal-body">
        <p class="warning-text">
          This will clear ALL data and reload the page as if you're a new user.
        </p>
        <p class="warning-details">
          • All drawings will be lost<br>
          • All settings will be reset<br>
          • All panel positions will be reset
        </p>
      </div>
      
      <div class="modal-footer">
        <button 
          class="cancel-button"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button 
          class="reset-button"
          @click="handleReset"
        >
          Reset
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">interface Props {
  isVisible: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  'cancel': []
  'confirm-reset': []
}>()

const handleCancel = () => {
  emit('cancel')
}

const handleReset = () => {
  emit('confirm-reset')
}

const handleOverlayClick = () => {
  handleCancel()
}</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(theme('colors.black'), 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  margin: 0;
  padding: 0;
}

.modal-content {
  background: theme('colors.creme.light');
  border-radius: theme('borderRadius.xl');
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid theme('colors.gray.200');
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: theme('colors.gray.800');
}

.modal-body {
  padding: 20px 24px;
}

.warning-text {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: theme('colors.gray.700');
  line-height: 1.5;
}

.warning-details {
  margin: 0;
  font-size: 13px;
  color: theme('colors.gray.500');
  line-height: 1.6;
}

.modal-footer {
  padding: 16px 24px 20px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid theme('colors.gray.200');
}

.cancel-button,
.reset-button {
  padding: 8px 16px;
  border-radius: theme('borderRadius.DEFAULT');
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease;
}

.cancel-button {
  background: theme('colors.creme.light');
  color: theme('colors.gray.500');
  border-color: theme('colors.gray.300');
}

.cancel-button:hover {
  background: theme('colors.gray.50');
  border-color: theme('colors.gray.400');
}

.reset-button {
  background: theme('colors.danger.DEFAULT');
  color: theme('colors.creme.light');
  border-color: theme('colors.danger.DEFAULT');
}

.reset-button:hover {
  background: theme('colors.danger.dark');
  border-color: theme('colors.danger.dark');
}
</style>